import React from 'react';
import * as stylex from '@stylexjs/stylex';
import '../theme/Colors.stylex';
import { ColorNeutral } from '../theme/Colors.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';
import '../theme/Spaces.stylex';
import { Gap } from '../theme/Spaces.stylex';
import type { t as Space } from '../theme/Spaces.gen';
import { Title } from '../typography/Title';
import { TextStyle } from '../typography/TextStyle';
import { CanaryCard, type CanaryCardPadding } from './CanaryCard';
import { Stack } from './Stack';

// MARK: Section
// =============
type SectionProps = {
  children: React.ReactNode;
  title?: string;
  accent?: boolean;
  space?: Space;
  padding?: CanaryCardPadding;
};
function SectionComponent({
  children,
  title,
  accent = false,
  space = 'large',
  padding
}: SectionProps) {
  return <div {...{
    0: {
      className: "display-xrvj5dj flexGrow-x1c4vz4f flexGrow-x1124wq1 minWidth-xeuugli borderRadius-x1qd5jr3",
      "data-style-src": "wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:164"
    },
    1: {
      className: "display-xrvj5dj flexGrow-x1c4vz4f flexGrow-x1124wq1 minWidth-xeuugli borderRadius-x1qd5jr3 borderWidth-xmkeg23 borderStyle-x1y0btm7 borderColor-x5bw2fz",
      "data-style-src": "wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:164; wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:173"
    }
  }[!!accent << 0]}>
      <CanaryCard padding={padding}>
        <Stack space={space}>
          {title ? <Title level={4} weight="semibold" color={ColorNeutral.textDefault}>
              {title}
            </Title> : null}
          {children}
        </Stack>
      </CanaryCard>
    </div>;
}
const MemoizedSectionComponent = React.memo(SectionComponent);
export { MemoizedSectionComponent as FieldsetLayoutPanelSection };
export type { SectionProps as FieldsetLayoutPanelSectionProps };

// MARK: Heading
// =============
type HeadingProps = {
  title: string;
  description: string;
};
function Heading({
  title,
  description
}: HeadingProps) {
  const empty = title === '' && description === '';
  return <div {...{
    0: {
      className: "flex-x9yhb9l flex-x110y9of minWidth-xeuugli",
      "data-style-src": "wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:151"
    },
    1: {
      className: "flex-x9yhb9l flex-x110y9of minWidth-xeuugli display-x1lliihq display-xkhwb2x",
      "data-style-src": "wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:151; wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:158"
    }
  }[!!empty << 0]}>
      {empty ? null : <Stack space="medium">
          {title !== '' ? <Title level={2} weight="bold">
              {title}
            </Title> : null}
          {description !== '' ? <TextStyle variation="normal" size="large" lineHeight="large">
              {description}
            </TextStyle> : null}
        </Stack>}
    </div>;
}

// MARK: root
// ==========
type Props = {
  children: React.ReactNode;
  title?: string;
  description?: string;
  accent?: boolean;
  space?: Space;
  padding?: CanaryCardPadding;
};
function Component({
  children,
  title = '',
  description = '',
  accent,
  space,
  padding
}: Props) {
  const sectioned = React.Children.toArray(children).some(child => React.isValidElement(child) && child.type === MemoizedSectionComponent);
  return <div className="containerType-x12h1iku width-xh8yej3" data-style-src="wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:129">
      <div className="display-x78zum5 flexDirection-x1q0g3np flexDirection-xufayd4 gap-x1xzqd0s gap-x16sa64b" data-style-src="wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:133">
        <Heading title={title} description={description} />
        <div className="display-x78zum5 flexDirection-xdt5ytf flex-x1cqoux5 gap-x1xzqd0s minWidth-xeuugli" data-style-src="wino-gc:src/resources/layout-and-structure/FieldsetLayoutPanel.tsx:144">
          {sectioned ? children : <MemoizedSectionComponent accent={accent} space={space} padding={padding}>
              {children}
            </MemoizedSectionComponent>}
        </div>
      </div>
    </div>;
}
const MemoizedComponent = React.memo(Component);
export { MemoizedComponent as FieldsetLayoutPanel };
export type { Props as FieldsetLayoutPanelProps };

// MARK: style
// ===========
const STACKED_LAYOUT_QUERY = '@container (max-width: 56rem)';